// Browser-side key generation + PKCS#10 CSR building. The private key is
// created in this browser and never sent anywhere — the CA only sees the CSR.
import * as asn1js from "asn1js";
import * as pkijs from "pkijs";

export interface GeneratedRequest {
  csrPem: string;
  privateKeyPem: string;
}

function derToPem(der: ArrayBuffer, label: string): string {
  const b64 = btoa(String.fromCharCode(...new Uint8Array(der)));
  const lines = b64.match(/.{1,64}/g) ?? [];
  return `-----BEGIN ${label}-----\n${lines.join("\n")}\n-----END ${label}-----\n`;
}

/** Generate a P-256 keypair and a CSR with CN (+ optional rfc822 SAN). */
export async function generateKeyAndCsr(
  cn: string,
  email?: string,
): Promise<GeneratedRequest> {
  const crypto = pkijs.getCrypto(true);
  const keyPair = (await crypto.generateKey(
    { name: "ECDSA", namedCurve: "P-256" },
    true,
    ["sign", "verify"],
  )) as CryptoKeyPair;

  const csr = new pkijs.CertificationRequest();
  csr.version = 0;
  // RFC 2986: the [0] attributes field is REQUIRED even when empty — pkijs
  // omits the whole block when `attributes` is undefined, which strict
  // parsers (rust asn1 in `cryptography`) reject with ShortData.
  csr.attributes = [];
  csr.subject.typesAndValues.push(
    new pkijs.AttributeTypeAndValue({
      type: "2.5.4.3", // commonName
      value: new asn1js.Utf8String({ value: cn }),
    }),
  );
  if (email) {
    const san = new pkijs.GeneralNames({
      names: [new pkijs.GeneralName({ type: 1, value: email })],
    });
    const extensions = new pkijs.Extensions({
      extensions: [
        new pkijs.Extension({
          extnID: "2.5.29.17",
          critical: false,
          extnValue: san.toSchema().toBER(false),
        }),
      ],
    });
    csr.attributes.push(
      new pkijs.Attribute({
        type: "1.2.840.113549.1.9.14", // extensionRequest
        values: [extensions.toSchema()],
      }),
    );
  }
  await csr.subjectPublicKeyInfo.importKey(keyPair.publicKey);
  await csr.sign(keyPair.privateKey, "SHA-256");

  const csrDer = csr.toSchema().toBER(false);
  const keyDer = await crypto.exportKey("pkcs8", keyPair.privateKey);
  return {
    csrPem: derToPem(csrDer, "CERTIFICATE REQUEST"),
    privateKeyPem: derToPem(keyDer as ArrayBuffer, "PRIVATE KEY"),
  };
}

/** Trigger a client-side download of text content. */
export function downloadText(filename: string, content: string): void {
  const blob = new Blob([content], { type: "application/x-pem-file" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
